import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { selectAuthToken, useAuthStore } from "../../store/useAuth";
import { handleAuthError } from "../../utils/auth-error-handler";
import { getAuthHeaders, isPublicEndpoint } from "../../utils/auth-utils";
import { buildApiUrl, MutationConfig } from "./hook-utils";

export const usePost = <T, TVariables = any>(
  endpoint: string,
  config?: MutationConfig
) => {
  const token = useAuthStore(selectAuthToken);
  const queryClient = useQueryClient();

  return useMutation<T, Error, TVariables>({
    mutationFn: async (data: TVariables) => {
      try {
        const url = buildApiUrl(endpoint);

        if (!url) {
          throw new Error("URL is not available");
        }

        if (!token && !isPublicEndpoint(endpoint)) {
          throw new Error("Authentication token is required");
        }

        const headers = {
          "Content-Type": "application/json",
          ...getAuthHeaders(endpoint, token),
        };

        const response = await fetch(url, {
          method: "POST",
          headers,
          body: JSON.stringify(data),
        });

        if (!response.ok) {
          // Try to parse error response from backend
          try {
            const errorText = await response.text();
            if (errorText) {
              const errorData = JSON.parse(errorText);
              if (
                "success" in errorData &&
                !errorData.success &&
                "message" in errorData
              ) {
                const error = new Error(errorData.message);
                (error as any).status = response.status;
                (error as any).data = errorData;
                throw error;
              }
            }
          } catch (parseError) {
            // If it's our custom error, re-throw it
            if (
              parseError instanceof Error &&
              parseError.message !== `HTTP error! status: ${response.status}`
            ) {
              throw parseError;
            }
            // Otherwise, fall through to generic error
          }

          const error = new Error(`HTTP error! status: ${response.status}`);
          (error as any).status = response.status;
          throw error;
        }

        const result = await response.json();

        // Check for error in response body (even with 200 status)
        if ("success" in result && !result.success && "message" in result) {
          const error = new Error(result.message);
          (error as any).status = response.status;
          (error as any).data = result;
          throw error;
        }

        // Handle CRM API response format
        if (result.success && result.data) {
          return result.data;
        }
        return result;
      } catch (error) {
        throw error;
      }
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [endpoint] });
      config?.onSuccess?.(data);
    },
    onError: (error, variables, context) => {
      // Check if this is an auth error and handle logout
      handleAuthError(error);

      if (config?.onError) {
        config.onError(error);
        return;
      }
      const message = (error as any)?.message || "Запрос не выполнен";
      toast.error(message);
    },
    ...(config?.onMutate && { onMutate: config.onMutate }),
  });
};
